// -----------------------------------------------------------------------------
// columnLabels.ts
//
// Display labels for report-builder columns whose SQL name misleads, and the set
// of columns the pickers don't offer — the ones that hold nothing, plus the ones
// deliberately withheld. Also what a cell READS as, for the rare column whose
// stored value is not what a reader should be shown (COLUMN_DISPLAY_VALUES).
//
// WHY: this schema has a documented, catalogued naming problem — "Hours" columns
// holding minutes, `ResourceNo` meaning an operation on one table and a person on
// the next, `Checked` holding a user code rather than a boolean. A report builder
// that shows raw column names hands every one of those traps straight to the user.
// Source of truth is `DATA_DICTIONARY.md` in the Function App repo, §8 in
// particular ("Fields whose contents don't match their names"); section references
// below point at it.
//
// DISPLAY ONLY, AND ONE DIRECTION. A label never becomes a value: the picker's
// value, the saved report's `Definition` JSON and the SQL the BFF whitelists all
// stay the real column name. Two columns may legitimately share a label, so this
// map must never be reverse-looked-up. That is what makes relabeling safe where
// renaming the columns would not be — every saved report keeps working untouched.
//
// A LOOKUP, NEVER THE LIST. Columns come from the live schema
// (`GET api/reports/columns`), so anything absent here falls through to
// `humanizeKey`, and a renamed or dropped column leaves a dead key rather than a
// broken picker. Keys match case-insensitively (`INFORMATION_SCHEMA` casing is not
// guaranteed).
//
// SCOPE: the tables the builder offers. Tables on
// `ReportQueryService.DeniedTables` (inspection, calibration, training, checklists,
// `JobOptions`, `UsedHours`, `LeakTestCertificates`, staging) are deliberately
// absent, which is why several §8 entries have no counterpart here. The blocks
// marked "NOW ON DeniedTables" are the exception — those tables were offered once
// and their labels are kept against the denial being lifted. Dead keys cost
// nothing; this is a lookup, not a list.
//
// WORDING: sentence case, first word only, recognised acronyms uppercase — the
// same rule `humanizeKey` follows and the app-wide header convention in AGENTS.md.
//
// ⚠️ AN OPERATION IS CALLED A **RESOURCE** IN THIS APP. The schema's "operation"
// (`Operations.ResourceNo`, `JobLogging.ResourceNo`, `Bridge.Operation`,
// `LoggedHours.Operation`, …) is the work step, and every screen the user already
// knows presents it as a Resource: the page is titled *Resource Settings*, the
// ribbon reads *Resources*, the shopfloor board has a *resource filter*, and
// `JobPage`/`RoutingPage`/`ToolPage` all render that column as `headerText=
// "Resource"`. Report headings follow the app, not the schema — so these labels
// say Resource, and adding a label for any new operation-bearing column means
// saying Resource there too.
//
// The one place that is NOT a resource is `Users.ResourceNo`, which is a PERSON
// (§8.2) — hence "User code". Keeping those two apart is the whole reason this
// map is keyed per table.
// -----------------------------------------------------------------------------

import { DisplayValue, humanizeKey } from "./reportHelpers";

/**
 * Columns that mean the same thing on every table that has them. Consulted only
 * after the per-table map, so a table can still override one (`Changelog` does,
 * for `ValidFrom`/`ValidTo` — there they describe the row that CHANGED, not this
 * row's own history).
 */
const GLOBAL_COLUMN_LABELS: Record<string, string> = {
  // The tenant/org discriminator on almost every table (§1.1). "WHOTO" means
  // nothing to anyone who hasn't read the schema; SSC is what the business calls
  // the site this value identifies (the data dictionary says "SSC-wide setting"
  // for the same idea on Options).
  WHOTO: "SSC",
  // Temporal period columns on the system-versioned tables (Users, Operations,
  // Options) — §1.7.
  ValidFrom: "Record valid from",
  ValidTo: "Record valid to",
  SysStartTime: "Record valid from",
  SysEndTime: "Record valid to",
};

/**
 * Per-table overrides, keyed `Table.Column`. Grouped by table, in the order the
 * data dictionary presents them.
 */
const COLUMN_LABELS: Record<string, string> = {
  // ---------------------------------------------------------------- Bridge ----
  // The ERP fact table (§2.1). One row per production order × routing step.
  // "Name of Operation" in the DB — the work step, which the app calls a resource.
  "Bridge.Operation": "Resource",
  "Bridge.RoutingNo": "Routing step",
  // §1.2: two warehouses, and "PO warehouse" reads as *purchase* order.
  "Bridge.POWarehouse": "Production order warehouse",
  "Bridge.Warehouse": "Material warehouse",
  // §8.4: both import paths set it to the same value as Status — and Status is the
  // one held out of the pickers (NOT_OFFERED below), so this is the single order
  // status the builder shows. Same value either way, hence no "(duplicate)".
  "Bridge.CustomerStatus": "Production order status",
  "Bridge.Description": "Production order description",
  "Bridge.EndDate": "Due date",
  // §8.4: initialised to EndDate, then owned by the scheduler — so on a tenant
  // whose scheduler hasn't run it still reads as the ERP due date.
  "Bridge.ScheduledEndDate": "Planned finish",
  "Bridge.StartDate": "Start date",
  "Bridge.CustDelDate": "Customer delivery date",
  // §8.4: a varchar label, not a number, and unrelated to JobLogging.Complexity.
  // The business reads this field as the BP's priority.
  "Bridge.JobComplexity": "BP priority",
  // §8.4: a shortage code (1, 10, NULL), not a boolean. Not offered — kept
  // labelled for the saved reports that already select it.
  "Bridge.Incomplete": "Material shortage code",
  // §8.4: latest material-availability date across the order's components.
  "Bridge.Date": "Material availability date",
  "Bridge.OrderNumber": "Sales order number",
  // Not offered — CustomerStatus above carries the same value under a better name.
  // The label stays for a saved report that already selects this column.
  "Bridge.Status": "ERP order status",
  "Bridge.Notes": "Order notes",
  "Bridge.PickRmrk": "Pick remarks",
  // §8.4: the PRODUCTION order's priority; the source field is PWPriority.
  "Bridge.POPriority": "Production order priority",
  // §8.4: char(1) 'Y'/'N'/blank, not a boolean.
  "Bridge.HasIssuedResources": "Materials issued (Y/N)",
  "Bridge.HasReportedCompletion": "Completion reported (Y/N)",
  "Bridge.ResourceUnit": "Quantity per resource unit",
  // §2.1: minutes planned for the whole order (`MinutesRequired` is this step's).
  // "Total minutes" reads like a roll-up of the rows on screen; it isn't one.
  "Bridge.TotalMinutes": "Planned minutes",

  // ------------------------------------------------------------ JobLogging ----
  // The board's own table (§2.2).
  "JobLogging.Job": "Production order",
  // §8.2, the schema's most overloaded name: here ResourceNo is the work step,
  // i.e. the app's Resource — not a person, as it is on Users.
  "JobLogging.ResourceNo": "Resource",
  "JobLogging.RoutingNo": "Routing step",
  "JobLogging.POWarehouse": "Production order warehouse",
  "JobLogging.Warehouse": "Material warehouse",
  // §8.1/§1.3: numeric(18,2) columns named "Hours" that hold MINUTES. The labels
  // no longer say so — "Time planned"/"Time logged" is the shopfloor's wording —
  // so the unit lives in the guide's "Reading the numbers" section instead.
  "JobLogging.HoursReqd": "Time planned",
  "JobLogging.HoursUsed": "Time logged",
  "JobLogging.EndDate": "Due date",
  // §8.3, the worst offender: written as Bridge.Incomplete, read as a user code,
  // tested as a flag. Labelled for the meaning the app surfaces — the entity's own
  // [Description("User Code")], and what Associate/UserName resolve it against.
  "JobLogging.Text": "User ID",
  "JobLogging.Date": "Material availability date",
  // NULL = still open. The board's most-used predicate.
  "JobLogging.OperDateCompleted": "Resource completed date",
  "JobLogging.ScheduledEndDate": "Planned finish",
  // §8.4: the RESOURCE's complexity (Operations.CPNumber), not the job's.
  "JobLogging.Complexity": "Resource complexity",
  // Named for a bench, but it holds the NAME of the toolset the job used — kept
  // beside the toolset's id so a past job still reads correctly after the toolset
  // is renamed or deleted. No screen shows it; this column is how it is reached.
  // An empty one reads "None recorded" (COLUMN_DISPLAY_VALUES below).
  "JobLogging.Station": "Toolset",
  // §8.4: only ever 'Rejected' or NULL, in a varchar(8000).
  "JobLogging.Status": "QA reject flag",
  // §8.4: a duplicate of HoursReqd — the Bridge.TotalMinutes sum is overwritten.
  "JobLogging.TotalMinutes": "Minutes required (duplicate)",

  // ------------------------------------------------------ ComponentPartData ----
  // BOM component demand per job (§2.3).
  "ComponentPartData.Job": "Production order",
  "ComponentPartData.StockCode": "Parent item",
  "ComponentPartData.LineNum": "BOM line number",
  "ComponentPartData.Warehouse": "Material warehouse",
  "ComponentPartData.POWarehouse": "Production order warehouse",
  "ComponentPartData.QtyReqd": "Quantity still required",
  "ComponentPartData.QtyAddit": "Additional quantity",
  "ComponentPartData.QtyATP": "Available-to-promise quantity",
  "ComponentPartData.EndDate": "Date component is needed",
  "ComponentPartData.PODueDate": "Material arrival date",
  // §2.3/§8.4: two near-identical names, different types. POQty is the numeric
  // allocated quantity; POQuantity is a display string, possibly comma-separated.
  "ComponentPartData.POQty": "PO quantity allocated",
  "ComponentPartData.POQuantity": "PO quantity (text)",
  // §8.4: a display list of PO numbers, so it can hold several in one cell. Do
  // not join on it — the guide's "two things not to join on" says so by label.
  "ComponentPartData.PurchaseOrder": "PO number",
  "ComponentPartData.Description": "Component description",
  "ComponentPartData.PickList": "Pick list number",

  // ----------------------------------------------------- PickedComponentData ----
  // Physical picking / issuing (§2.4).
  "PickedComponentData.Job": "Production order",
  "PickedComponentData.StockCode": "Parent item",
  "PickedComponentData.LineNum": "BOM line number",
  "PickedComponentData.Warehouse": "Warehouse picked from",
  "PickedComponentData.IssueNo": "Material issue document number",
  "PickedComponentData.DateCreated": "Date created",
  "PickedComponentData.DateUpdated": "Last changed by import",
  // §8.4: seeded from ComponentPartData.QtyPlanned / QtyIssued, so neither name
  // says what it holds.
  "PickedComponentData.QtyOrdered": "Quantity to pick",
  "PickedComponentData.QtyPicked": "Quantity issued",
  "PickedComponentData.Description": "Component description",
  // §8.4: who/what confirmed the pick — a varchar(50) code, NOT a boolean.
  "PickedComponentData.Checked": "Confirmed by",

  // ------------------------------------------------------- OnHandSnapshot ----
  // Stock on hand (§2.5). Note the allocation pass decrements these in place, so
  // between imports they reflect allocation rather than reality.
  "OnHandSnapshot.QtyOnHand": "Quantity on hand",
  "OnHandSnapshot.QtyInReceiving": "Quantity in receiving",

  // ----------------------------------------------------------- POSnapshot ----
  // Open purchase order lines, legacy import path only (§2.6).
  "POSnapshot.LineNumber": "PO line number",
  "POSnapshot.DueDate": "Expected arrival date",
  "POSnapshot.StockCode": "Material on order",
  "POSnapshot.QtyOrdered": "Quantity on order",
  "POSnapshot.QtyAllocated": "Quantity committed to jobs",

  // ------------------------------------------------------------- JobNotes ----
  // Notes against a production order (§2.7).
  "JobNotes.Job": "Production order",
  // The column that decides whether the import may replace the note.
  "JobNotes.Type": "Note source (SAP or CSM)",
  "JobNotes.LineNum": "Line number",
  "JobNotes.Comment": "Note text",

  // --------------------------------------------- ProductionOrderRelations ----
  // Parent/child production orders (§2.8).
  "ProductionOrderRelations.ProductionOrderNumber": "Child production order",
  "ProductionOrderRelations.ParentProductionOrderNumber": "Parent production order",

  // ------------------------------------------- CompletedProductionOrders ----
  // The closure record — this table IS the definition of "closed" (§3.1).
  "CompletedProductionOrders.Job": "Production order",
  "CompletedProductionOrders.Quantity": "Quantity completed",
  // §8.4: UTC, with no "Utc" anywhere in the name.
  "CompletedProductionOrders.PWDateCompleted": "Date completed (UTC)",
  "CompletedProductionOrders.CompletedBy": "Completed by (user code)",
  "CompletedProductionOrders.ClosedBy": "Closed by (user code)",

  // ---------------------------------------------------------- JobPlanning ----
  // Delivery-confidence overlay (§3.3). NOW ON DeniedTables — kept so the labels
  // come back with the table if the denial is ever lifted.
  "JobPlanning.Job": "Production order",
  "JobPlanning.ScheduledEndDate": "Order planned finish",
  "JobPlanning.POWarehouse": "Production order warehouse",
  "JobPlanning.DeliveryConfidenceStatus": "Delivery confidence",
  "JobPlanning.NotComplyReason": "Reason for missed date",

  // ----------------------------------------------------------- AvailHours ----
  // Daily capacity per user (§3.5).
  // §8.2: the DB's own description says "User Id". It is not a machine.
  "AvailHours.Machine": "User",
  // Genuinely hours here — which is why the × 60 appears wherever this meets the
  // minutes-bearing columns (§1.3).
  "AvailHours.AvailHours": "Available hours",

  // ------------------------------------------- ResourceRecurrenceCapacity ----
  // The recurring pattern AvailHours rows are generated from (§3.6). NOW ON
  // DeniedTables; kept for the same reason as JobPlanning above.
  // §8.2: the DB's own description says "User ID".
  "ResourceRecurrenceCapacity.ResourceCode": "User",
  "ResourceRecurrenceCapacity.From": "Pattern start date",
  "ResourceRecurrenceCapacity.To": "Pattern end date",
  "ResourceRecurrenceCapacity.AvailableHours": "Available hours per occurrence",

  // --------------------------------------------------------- POAllocation ----
  // PO-to-job allocation ledger, legacy import path only (§3.7).
  "POAllocation.LineNumber": "PO line number",
  "POAllocation.Job": "Production order",
  "POAllocation.ComponentPart": "Material",
  "POAllocation.QtyAllocated": "Quantity committed",

  // ---------------------------------------------------------- LoggedHours ----
  // The labour ledger (§4.1).
  "LoggedHours.ID": "Row ID",
  "LoggedHours.Job": "Production order",
  // §8.2: holds Users.ResourceNo — a code, not a username and not an email.
  "LoggedHours.Username": "User code",
  "LoggedHours.LoginDate": "Started at",
  // NULL here means "still running", and elapsed time is measured to now.
  "LoggedHours.LogoutDate": "Stopped at (blank = still running)",
  // The work step the time was booked against → JobLogging.ResourceNo.
  "LoggedHours.Operation": "Resource",
  "LoggedHours.Rework": "Rework session",

  // ---------------------------------------------------------------- Users ----
  // People (§5.1). Shares its shape with Operations, which is where most of the
  // confusion comes from.
  "Users.ResourceNo": "User code",
  "Users.ResourceDescription": "Name",
  // §8.4: the RLS tenant/org code — and it is also selected AS Password.
  "Users.ResourceAlias": "SSC code (tenant)",
  // §8.4: a discriminator ('RES' = person), not a unit of measure. Not offered —
  // every row on this table is a person, so the column answers nothing.
  "Users.ResourceUOM": "Record type (RES = person)",
  // §8.4: complexity AND the authorisation bitmask — (complexity & 1) gates the
  // manager-only screens.
  "Users.CPNumber": "Complexity / permissions",
  // §8.4: a bitmask (1 capacity, 2 skills, 4 shopfloor), not a boolean.
  "Users.Hidden": "Hidden from (bitmask)",
  "Users.Disabled": "Account disabled",
  // §8.4: load-bearingly the user's WAREHOUSE — joined to JobLogging.POWarehouse.
  // The print-location origin of the column is a schema detail, not a heading.
  "Users.PrintLocation": "Warehouse",
  "Users.SortOrder": "Display order",
  // Acronym, so `humanizeKey` would give "Tos accepted date" — it isn't a word.
  "Users.TosAcceptedDate": "TOS accepted date",

  // ----------------------------------------------------------- Operations ----
  // Work steps (§5.1) — the app's *Resources*, maintained on Resource Settings.
  // The gate on what reaches the shopfloor.
  "Operations.ResourceNo": "Resource code",
  "Operations.ResourceDescription": "Resource name",
  // Unlike on Users, here this really is a display alias (§8.4).
  "Operations.ResourceAlias": "Display alias",
  "Operations.ResourceUOM": "Record type (MIN = resource)",
  "Operations.CPNumber": "Resource complexity",
  // §8.1: minutes, in a `time` column.
  "Operations.DailyCycleTime": "Daily minutes achievable",
  "Operations.Inspection": "Is an inspection step",
  "Operations.InspectionRequired": "Requires an inspection result",
  "Operations.CompleteProcedureType": "Completion form type",
  "Operations.SortOrder": "Display order",

  // -------------------------------------------------------------- Options ----
  // Every SSC-wide setting (§5.2). NOW ON DeniedTables; kept.
  "Options.Application": "Setting group",
  "Options.Title": "Setting name",
  // §8.4: always text — booleans are "True"/"False" in new rows, 1/yes/0 in old.
  "Options.Value": "Value (always text)",

  // ------------------------------------------------------- UserOperations ----
  // Which resources a user may work (§5.3).
  // §8.2: a user here, a resource on the Operation column beside it — the two
  // senses of "resource" sitting in adjacent columns of one table.
  "UserOperations.Operation": "Resource",
  "UserOperations.ResourceNo": "User code",
  // §8.4: per-ACTION permission, not a category — "LogTime" or "CompleteJob".
  "UserOperations.Type": "Permission (log time / complete)",

  // ------------------------------------------------------ OperationGroups ----
  // Groups of resources, for shift planning (§5.3). NOW ON DeniedTables; kept.
  "OperationGroups.Id": "Resource group ID",
  "OperationGroups.Name": "Resource group name",
  "OperationGroups.IsDefault": "Is the default group",
  "OperationGroups.Description": "Resource group description",
  "OperationGroups.Colour": "Planner colour",

  // -------------------------------------------------- UserOperationGroups ----
  // A dated roster (§5.3).
  "UserOperationGroups.OperationGroupId": "Resource group ID",
  "UserOperationGroups.ResourceNo": "User code",
  "UserOperationGroups.Date": "Roster date",
  "UserOperationGroups.Shift": "Shift (AM / PM)",

  // ----------------------------------------------------- OperationFilters ----
  // A saved board filter (§5.3).
  "OperationFilters.Id": "Filter ID",
  "OperationFilters.Name": "Filter name",
  "OperationFilters.Description": "Filter description",

  // -------------------------------------------- OperationOperationFilters ----
  "OperationOperationFilters.OperationFilterId": "Board filter ID",
  // §8.2: a work step, despite the name it shares with the user columns.
  "OperationOperationFilters.ResourceNo": "Resource",

  // ------------------------------------------------------- UserPreferences ----
  // NOW ON DeniedTables; kept.
  "UserPreferences.ResourceNo": "User code",
  "UserPreferences.OperationFilterId": "Selected board filter",

  // ------------------------------------------------------------ ReworkLog ----
  // The rework audit trail (§6). Deliberately denormalized — the part/BP columns
  // are copied from Bridge at write time so history still reads correctly.
  "ReworkLog.Id": "Row ID",
  "ReworkLog.Job": "Production order",
  "ReworkLog.ResourceNo": "Resource reworked",
  "ReworkLog.RoutingNo": "Routing step",
  "ReworkLog.Reason": "Rework reason",
  "ReworkLog.StockCode": "Part (at time of rework)",
  "ReworkLog.CustomerCode": "BP code (at time of rework)",
  "ReworkLog.CustomerName": "BP name (at time of rework)",
  "ReworkLog.InitiatedByEmail": "Raised by (email)",
  "ReworkLog.InitiatedByCode": "Raised by (user code)",
  "ReworkLog.InitiatedByName": "Raised by (name)",
  "ReworkLog.CreatedDate": "Rework raised (UTC)",

  // --------------------------------------------------------- SavedReports ----
  "SavedReports.Id": "Report ID",
  "SavedReports.Name": "Report name",
  "SavedReports.Definition": "Report definition (JSON)",
  "SavedReports.OwnerEmail": "Owner email",
  "SavedReports.CreatedDate": "Created (UTC)",
  "SavedReports.ModifiedDate": "Last modified (UTC)",

  // ---------------------------------------------------- SavedReportShares ----
  "SavedReportShares.Id": "Row ID",
  "SavedReportShares.ReportId": "Report ID",
  "SavedReportShares.UserEmail": "Shared with (email)",

  // ------------------------------------------------------------ Changelog ----
  // DB-side change log, populated by triggers (§7). ValidFrom/ValidTo here
  // describe the row that CHANGED, so they override the global labels.
  "Changelog.LogId": "Log row ID",
  "Changelog.TableName": "Table changed",
  // §8.4: the user's email from SESSION_CONTEXT, not a session id.
  "Changelog.SessionIDValue": "Changed by (email)",
  "Changelog.TimeStampUtc": "Logged at (UTC)",
  "Changelog.ValidFrom": "Changed row valid from",
  "Changelog.ValidTo": "Changed row valid to",
  "Changelog.PKKey": "Changed row key",

  // ------------------------------------------------------------ ErrorLogs ----
  // Still written by the live Function App: `JobsService.LogError` / `JobViewer
  // .LogError` insert a row for every failed job action (start, stop, complete,
  // rework, notes, inspection) before throwing. Insert-only from the app's side —
  // this report builder is the one read path — and App Insights carries everything
  // else (§7).
  "ErrorLogs.ID": "Error log ID",
  // Environment.MachineName at the time: the Function App instance, not a shopfloor PC.
  "ErrorLogs.Machine": "Server instance",
  "ErrorLogs.LoggedUser": "User",
  "ErrorLogs.LogDate": "Logged at",
  "ErrorLogs.Message": "Error message",

  // -------------------------------------------------------- LockedActions ----
  // A mutex table — a row's existence IS the lock (§7).
  "LockedActions.Action": "Locked action",
  // §8.4: when the lock was acquired, not the start of a range.
  "LockedActions.StartDate": "Lock acquired at",
  "LockedActions.User": "Lock held by",

  // ------------------------------------------------------- WorkflowStates ----
  // Short-lived scratch state; purged after a day (§5.6). NOW ON DeniedTables; kept.
  "WorkflowStates.Id": "Instance ID",
  "WorkflowStates.Date": "Created at",
  "WorkflowStates.State": "Progress (serialized)",
};

/**
 * Columns that hold nothing — data dictionary §8.7 ("Columns that hold nothing"),
 * restricted to tables the builder offers.
 *
 * §8.7 mixes two cases and so does this set, deliberately: columns the writer
 * always leaves `NULL` (or at a constant default), and columns no code path reads
 * or writes but which may still hold values from the desktop era. Both are
 * confusing to offer in a report; neither is worth a column of nulls. To bring one
 * back, delete its line — nothing else depends on this.
 */
const EMPTY_COLUMNS: string[] = [
  // Written by nothing; Options.LastSyncTime is the real import timestamp.
  "Bridge.UploadDate",
  // The merge always writes NULL — which is what makes GetNotes' ITEM branch
  // permanently empty.
  "ComponentPartData.Notes",
  // The merge always writes NULL.
  "ComponentPartData.QtyPickList",
  // Written by nothing, though GetComponents reads ISNULL(DueDate, PODueDate) —
  // so it always falls back.
  "ComponentPartData.DueDate",
  // Not on the entity, not in the insert: always its '02' default.
  "JobPlanning.Warehouse",
  // Superseded by SavedReportShares; unmapped, always its default.
  "SavedReports.IsShared",
  // Inherited from the shared resource shape and meaningless for a person.
  "Users.InspectionRequired",
  "Users.StartUpTime",
  "Users.WaitingTime",
  "Users.EndTime",
  "Users.DailyCycleTime",
  // Read by nothing.
  "Operations.StartUpTime",
  "Operations.WaitingTime",
  "Operations.EndTime",
  // Not on the entity and not selected — unreachable from the app, so whatever
  // they hold cannot take effect.
  "Operations.Hidden",
  "Operations.Disabled",
];

/**
 * Columns that DO hold data but are withheld by product decision — internals,
 * duplicates of a column already on offer, and settings nobody reports on.
 *
 * The distinction from `EMPTY_COLUMNS` is only documentary: both lists resolve
 * into the same `NOT_OFFERED` set, are held out of the same pickers, and the guide
 * lists neither. Keeping them apart says *why* a column is absent — "it is always
 * null" is a fact about the database, "we decided not to offer it" is a decision
 * someone may want to revisit. Reversing one is deleting its line.
 *
 * Note the labels above survive for these columns on purpose: a column can still
 * reach the screen through a saved report that selected it before it was withheld,
 * and it needs a heading when it does.
 */
const HIDDEN_COLUMNS: string[] = [
  // Bridge.CustomerStatus holds the same value under a clearer name (§8.4).
  "Bridge.Status",
  // A shortage CODE (1/10/NULL) that reads as a flag — of no use in a report and
  // actively misleading if summed or filtered as a boolean (§8.4).
  "Bridge.Incomplete",
  // The RESOURCE's complexity, not the job's, and only the scheduler reads it.
  "JobLogging.Complexity",
  // Only ever 'Rejected' or NULL, in a varchar(8000) — a flag pretending to be a
  // status (§8.4).
  "JobLogging.Status",
  // A duplicate of HoursReqd ("Time planned") — the Bridge.TotalMinutes sum is
  // overwritten by CAST(HoursReqd AS INT) on every import (§8.4).
  "JobLogging.TotalMinutes",
  // The ID of the job's toolset, whose NAME is on offer as "Toolset" (Station).
  // It points into the inspection tables, which the builder doesn't offer, and
  // two of its values are not toolsets at all: 0 (none recorded) and 9999 (not
  // asked) — a number that says less than the name beside it, and misleads.
  "JobLogging.InspectionToolSetId",
  // Every row on Users is a person ('RES'), so the discriminator answers nothing.
  "Users.ResourceUOM",
  // Inspector level / inspection requirement: the inspection tables are denied, and
  // InspectionRequired is read by no query at all.
  "Users.Inspection",
  // UI ordering, not data.
  "Users.SortOrder",
  // A bitmask of which SCREENS hide the user (§8.4) — unreadable as a number.
  "Users.Hidden",
  // Support-tooling state: which org a CORP support user last switched into.
  "Users.LastSupportTenant",
  // Complexity AND the permission bitmask in one int (§8.4) — neither is legible
  // as a report column.
  "Operations.CPNumber",
  // A scheduler input, in a `time` column holding minutes (§8.1), and one that
  // falls back to the user's capacity whenever it is 0 or unset.
  "Operations.DailyCycleTime",
  // Both drive the inspection flow, whose tables the builder doesn't offer.
  "Operations.Inspection",
  "Operations.InspectionRequired",
  // Which completion FORM the shopfloor gets — behaviour, not a fact about the work.
  "Operations.CompleteProcedureType",
  // Whether Start Job and Complete Job ASK for a toolset on this resource — a
  // setting again. What a job actually used is JobLogging.Station ("Toolset").
  "Operations.ToolsetsEnabled",
];

/**
 * Every column the pickers and the guide leave out, from both lists above.
 *
 * NOT OFFERED IS NOT THE SAME AS NOT QUERYABLE, and the difference is the safety
 * rule: this set is consulted for the PICKERS (and for the guide's field list),
 * never for `selectFields` loaded from a stored definition. A saved report that
 * already selects one of these keeps showing it — silently dropping a column from
 * someone's existing report would be the worse failure by far.
 */
const NOT_OFFERED_COLUMNS: string[] = [...EMPTY_COLUMNS, ...HIDDEN_COLUMNS];

/** What the Toolset column reads when the job row names no toolset. */
const NO_TOOLSET_RECORDED = "None recorded";

/**
 * The station text a web app sends, beside the id 9999, when it auto-completes a
 * resource without asking which toolset was used. A placeholder, not a toolset's
 * name.
 */
const AUTO_COMPLETE_PLACEHOLDER = "AutoComplete Station";

/**
 * `JobLogging.Station`: the three ways a job row says "no toolset" all read the
 * same. NULL is the usual one (nobody asked, none was chosen, or the job was
 * reworked); an empty string says the same thing; and the auto-complete
 * placeholder names nothing. "Empty" is read to include a value made only of
 * whitespace, deliberately: there is nothing in it to read.
 *
 * ANY OTHER VALUE IS SHOWN AS STORED. The placeholder is compared with the STORED
 * string, exactly — never trimmed, never case-folded — because the Function App
 * writes a toolset's name to the job row as it is given, so a value that differs
 * from the placeholder by one space may be a real name, and must not be swallowed
 * for resembling it. That also covers a `?` standing where a character fell
 * outside the column's code page (it is `varchar`; a toolset's name is
 * `nvarchar`). That is a storage limit, and a report shows what is stored — do not
 * mask it.
 */
const toolsetDisplayValue: DisplayValue = (stored) => {
  if (stored === null || stored === undefined) return NO_TOOLSET_RECORDED;
  if (typeof stored !== "string") return stored;
  return stored.trim() === "" || stored === AUTO_COMPLETE_PLACEHOLDER
    ? NO_TOOLSET_RECORDED
    : stored;
};

/**
 * What a column's cells READ as, where the stored value is not what a reader
 * should be shown. Keyed `Table.Column` like the labels, and held to the same
 * contract:
 *
 * DISPLAY ONLY, AND ONE DIRECTION. A display value never becomes a value. A
 * filter or a group-by on the column still goes to the BFF as the real column
 * and is answered from what is STORED — so a filter for "None recorded" finds
 * nothing (the rows holding NULL are the operator "is" with its value left
 * empty), and a report grouped by the column can show more than one "None
 * recorded" row, one for each stored value that reads that way.
 *
 * WHERE IT IS APPLIED: to a result's rows, once, when the report is run
 * (`execute` in ReportPage.tsx, through `buildDisplayValues` and
 * `applyDisplayValues` in reportHelpers.ts) — so the grid, the chart and the
 * export all read it, and so does a saved report run again later. A roll-up of
 * the column (a count, a max) is never touched.
 *
 * A LOOKUP, NEVER THE LIST, as above: a column absent here shows what it stores.
 */
const COLUMN_DISPLAY_VALUES: Record<string, DisplayValue> = {
  "JobLogging.Station": toolsetDisplayValue,
};

const lower = (s: string) => s.trim().toLowerCase();

const BY_TABLE_COLUMN = new Map<string, string>(
  Object.entries(COLUMN_LABELS).map(([key, label]) => [lower(key), label])
);
const BY_COLUMN = new Map<string, string>(
  Object.entries(GLOBAL_COLUMN_LABELS).map(([key, label]) => [lower(key), label])
);
const NOT_OFFERED = new Set<string>(NOT_OFFERED_COLUMNS.map(lower));
const DISPLAY_BY_TABLE_COLUMN = new Map<string, DisplayValue>(
  Object.entries(COLUMN_DISPLAY_VALUES).map(([key, rule]) => [lower(key), rule])
);

/**
 * What to call `table`.`field` on screen: the per-table override, else a
 * same-everywhere override, else the humanized column name.
 *
 * `table` is optional so a caller that only has a column name still gets the
 * global overrides and the humanized fallback.
 */
export function labelFor(table: string | null | undefined, field: string | null | undefined): string {
  if (!field || !field.trim()) return "";
  const column = lower(field);
  if (table && table.trim()) {
    const scoped = BY_TABLE_COLUMN.get(`${lower(table)}.${column}`);
    if (scoped) return scoped;
  }
  return BY_COLUMN.get(column) ?? humanizeKey(field.trim());
}

/**
 * True when this column is not offered — because it holds nothing
 * (`EMPTY_COLUMNS`) or because it is withheld (`HIDDEN_COLUMNS`). The caller
 * doesn't need to know which; both are absent from the pickers and the guide.
 */
export function isHiddenColumn(table: string | null | undefined, field: string | null | undefined): boolean {
  if (!table || !field) return false;
  return NOT_OFFERED.has(`${lower(table)}.${lower(field)}`);
}

/**
 * Drop the not-offered columns from one table's schema list. Use this on every
 * PICKER source; never on a saved report's selected fields.
 */
export function selectableColumns<T extends { name: string }>(
  table: string,
  columns: readonly T[] | undefined
): T[] {
  return (columns ?? []).filter((c) => !isHiddenColumn(table, c.name));
}

/**
 * How `table`.`field`'s cells should read, or `undefined` for a column that
 * shows what it stores — which is nearly all of them. Per table and
 * case-insensitive, like `labelFor`, but with no same-everywhere fallback: a
 * display value is a statement about one column's contents.
 */
export function displayValueFor(
  table: string | null | undefined,
  field: string | null | undefined
): DisplayValue | undefined {
  if (!table || !field) return undefined;
  return DISPLAY_BY_TABLE_COLUMN.get(`${lower(table)}.${lower(field)}`);
}

/**
 * The labels and the not-offered columns, flattened for the report composer
 * (`POST api/assist/report`, sent by `ApiService.askReportDefinition`).
 *
 * WHY THEY TRAVEL: the model that writes a proposed report sees the schema's raw
 * names, and in this schema the names are exactly what misleads (see the note at
 * the top). Shown the label beside each column, it matches "time planned" to
 * `HoursReqd` and writes its notes in the words on screen. And a not-offered
 * column must not be proposed at all — the person would get a report built on a
 * column they cannot find in any picker.
 *
 * STILL ONE OWNER. The Function App keeps no copy: it takes these per request,
 * and can only use them to DESCRIBE (a label is words in a prompt or a sentence,
 * never a name that reaches SQL) or to NARROW (a hidden column is removed from
 * what the model may choose). Keys: `Table.Column` for a per-table label,
 * `*.Column` for one that applies on every table.
 */
export function assistVocabulary(): { labels: Record<string, string>; hidden: string[] } {
  const labels: Record<string, string> = {};

  Object.entries(GLOBAL_COLUMN_LABELS).forEach(([column, label]) => {
    labels[`*.${column}`] = label;
  });
  Object.entries(COLUMN_LABELS).forEach(([key, label]) => {
    labels[key] = label;
  });

  return { labels, hidden: [...NOT_OFFERED_COLUMNS] };
}
